import '@unocss/reset/tailwind.css'
import '@arkit/ew-vue-components/dist/style.css'
;('./assets/main.css')
// import './assets/scss/main.scss'
import 'primevue/resources/primevue.min.css'
import 'primevue/resources/themes/aura-light-blue/theme.css' // theme can be changed to suit your preference
import 'virtual:uno.css'
import './web-component'

import { createApp, type App } from 'vue'

import { createRouterInstance } from './router';

import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import {
    easyWebComponents,
    EwPanel,
    EwPanelContent,
    EwIcon,
    EwPopover,
    EwModal,
    EwButton
} from '@arkit/ew-vue-components'

import AppComponent from './App.vue'

import MultiSelect from 'primevue/multiselect'
import DialogModal from 'primevue/dialog'
//import SoundPlayer from "vue3-sound-player";
// import SmallSoundPlayer from "vue3-sound-player/src/components/smallSoundPlayer.vue";
import BigSoundPlayer from './components/vue3-sound-player/src/components/SoundPlayer.vue'
import InteractiveImage from 'primevue/image'
import PaginatorComponent from 'primevue/paginator'
import PostGallery from 'primevue/galleria'
import Tooltip from 'primevue/tooltip'
import QuizStepper from 'primevue/stepper'
import StepperPanel from 'primevue/stepperpanel'
import QuizCheck from 'primevue/checkbox'
import QuizRadioButton from 'primevue/radiobutton'
import MessageToast from 'primevue/toast'
import ToastService from 'primevue/toastservice'
import Menu from 'primevue/menu'
import SpeedDial from 'primevue/speeddial'
import ViewMore from 'primevue/inplace'
import Menubar from 'primevue/menubar'
import Swal from 'sweetalert2'
import ConfirmationService from 'primevue/confirmationservice'
import Card from 'primevue/card'
import MeterGroup from 'primevue/metergroup'
//@ts-ignore
import Howl from 'howler'
import AccordionPrimeVue from 'primevue/accordion'
import AccordionTab from 'primevue/accordiontab'
import TieredMenu from 'primevue/tieredmenu'
import Editor from 'primevue/editor'
import Rating from 'primevue/rating'
import ProgressSpinner from 'primevue/ProgressSpinner'
import Ripple from 'primevue/ripple'
import Sidebar from 'primevue/sidebar'
import DropdownStatus from 'primevue/dropdown'
import Inplace from 'primevue/inplace'
import Calendar from 'primevue/calendar'
import type { AppConfigDefinition } from './types';
import { DefaultConfig } from './config';

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

const deepMerge = <T>(base: T, override?: Partial<T>): T => {
    if (!override) {
        return base
    }

    const baseRecord = isPlainObject(base) ? (base as Record<string, unknown>) : {}
    const overrideRecord = isPlainObject(override) ? (override as Record<string, unknown>) : {}
    const result: Record<string, unknown> = { ...baseRecord }

    Object.entries(overrideRecord).forEach(([key, overrideValue]) => {
        const baseValue = result[key]
        if (isPlainObject(baseValue) && isPlainObject(overrideValue)) {
            result[key] = deepMerge(baseValue, overrideValue)
            return
        }
        result[key] = overrideValue
    })

    return result as T
}

type RuntimeAppConfigDefinition = AppConfigDefinition & {
    __customConfigSource?: AppConfigDefinition
    __customConfigWindowKey?: string
    __rootSelector?: string
}

type MjcRuntimeEntry = {
    setLanguage: (lng: string) => void
}

type MjcRuntimeRegistry = Record<string, MjcRuntimeEntry> & {
    setLanguageForAll?: (lng: string) => void
}

const findWindowConfigKey = (customConfig?: AppConfigDefinition): string | undefined => {
    if (typeof window === 'undefined' || !customConfig) {
        return undefined
    }

    return Object.keys(window).find((key) => {
        if (key !== 'MjcConfig' && !key.startsWith('MjcConfig-')) {
            return false
        }
        return (window as unknown as Record<string, unknown>)[key] === customConfig
    })
}

const getRuntimeId = (selector: string, windowConfigKey?: string): string => {
    if (windowConfigKey?.startsWith('MjcConfig-')) {
        return windowConfigKey.replace(/^MjcConfig-/, '')
    }
    return selector.replace(/^#/, '').replace(/^latest-post-/, '')
}

const ensureMjcRuntime = (): MjcRuntimeRegistry => {
    const runtime = (window.MjcRuntime ?? {}) as MjcRuntimeRegistry
    runtime.setLanguageForAll = (lng: string) => {
        const nextLng = String(lng ?? '').trim()
        Object.keys(window).forEach((key) => {
            if (key === 'MjcConfig' || key.startsWith('MjcConfig-')) {
                const windowConfig = (window as unknown as Record<string, Partial<AppConfigDefinition> | undefined>)[key]
                if (windowConfig) {
                    windowConfig.defaultLng = nextLng as AppConfigDefinition['defaultLng']
                }
            }
        })
        Object.entries(runtime).forEach(([key, entry]) => {
            if (key !== 'setLanguageForAll' && typeof entry?.setLanguage === 'function') {
                entry.setLanguage(nextLng)
            }
        })
        window.dispatchEvent(new CustomEvent('mjc-config-updated', {
            detail: { defaultLng: nextLng }
        }))
    }
    window.MjcRuntime = runtime
    return runtime
}

const registerRuntimeEntry = (
    runtimeId: string,
    config: RuntimeAppConfigDefinition,
    customConfig?: AppConfigDefinition
) => {
    if (typeof window === 'undefined') {
        return
    }

    const runtime = ensureMjcRuntime()
    runtime[runtimeId] = {
        setLanguage(lng: string) {
            const nextLng = String(lng ?? '').trim()
            config.defaultLng = nextLng as AppConfigDefinition['defaultLng']
            if (customConfig) {
                customConfig.defaultLng = nextLng as AppConfigDefinition['defaultLng']
            }
            const windowConfigKey = config.__customConfigWindowKey
            if (windowConfigKey) {
                const windowConfig = (window as unknown as Record<string, Partial<AppConfigDefinition> | undefined>)[windowConfigKey]
                if (windowConfig) {
                    windowConfig.defaultLng = nextLng as AppConfigDefinition['defaultLng']
                }
            }
            window.dispatchEvent(new CustomEvent('mjc-config-updated', {
                detail: {
                    id: runtimeId,
                    defaultLng: nextLng
                }
            }))
        }
    }
}

export function createReaderInstance(args:{selector: string, customConfig?: AppConfigDefinition}) {
    const app = createApp(AppComponent)
	const {selector, customConfig} = args
	const config = deepMerge(DefaultConfig, customConfig) as RuntimeAppConfigDefinition

    Object.defineProperties(config, {
        __customConfigSource: {
            value: customConfig,
            enumerable: false,
            configurable: false,
        },
        __customConfigWindowKey: {
            value: findWindowConfigKey(customConfig),
            enumerable: false,
            configurable: false,
        },
        __rootSelector: {
            value: selector,
            enumerable: false,
            configurable: false,
        },
    })
    registerRuntimeEntry(getRuntimeId(selector, config.__customConfigWindowKey), config, customConfig)

    app.component('EwPanel', EwPanel)
    app.component('EwPanelContent', EwPanelContent)
    app.component('EwModal', EwModal)
    app.component('EwIcon', EwIcon)
    app.component('EwPopover', EwPopover)
    app.component('EwButton', EwButton)
    app.component('MultiSelect', MultiSelect)
    app.component('AccordionPrimeVue', AccordionPrimeVue)
    app.component('AccordionTab', AccordionTab)
    // app.component('SmallSoundPlayer', SmallSoundPlayer)
    app.component('BigSoundPlayer', BigSoundPlayer)
    app.component('DialogModal', DialogModal)
    app.component('InteractiveImage', InteractiveImage)
    app.component('PaginatorComponent', PaginatorComponent)
    app.component('PostGallery', PostGallery)
    app.component('QuizStepper', QuizStepper)
    app.component('StepperPanel', StepperPanel)
    app.component('QuizCheck', QuizCheck)
    app.component('QuizRadioButton', QuizRadioButton)
    app.component('Menu', Menu)
    app.component('SpeedDial', SpeedDial)
    app.component('ViewMore', ViewMore)
    app.component('Menubar', Menubar)
    app.component('MeterGroup', MeterGroup)
    app.component('Card', Card)
    app.component('Swal ', Swal)
    app.component('MessageToast', MessageToast)
    app.component('Howl', Howl)
    app.component('TieredMenu', TieredMenu)
    app.component('EditorComp', Editor)
    app.component('Rating', Rating)
    app.component('Sidebar', Sidebar)
    app.component('DropdownStatus', DropdownStatus)
    app.component('ProgressSpinner', ProgressSpinner)
    app.component('Inplace', Inplace)
    app.component('Calendar', Calendar)

    app.directive('ripple', Ripple)
    app.directive('tooltip', Tooltip)

    app.use(easyWebComponents)

    app.use(PrimeVue, { ripple: true })
    app.use(ToastService)
    const router = createRouterInstance(config.forceDetailsId);

    app.use(router)
    app.use(ConfirmationService)


	// * Start provides for global config
	app.provide("customConfig", config);

	const pinia = createPinia();
    app.use(pinia)

	// * Finally mount the app
	// console.log('Mounting app to ', selector)
	app.mount(selector)
	
    return app
}

// * Aiugment the window object
declare global {
	interface Window {
		createReaderInstance: (args:{selector: string, customConfig?: AppConfigDefinition}) => App<Element>
	}
}

window.createReaderInstance = createReaderInstance
